import { NavigateBefore, NavigateNext } from '@material-ui/icons'
import { GatsbyImage } from 'gatsby-plugin-image'
import React, { createRef } from 'react'
import Slider from 'react-slick'
import styled from 'styled-components'
import AnimateText from '../../helpers/animateText'
import { H2 } from '../Typography/Typography'
import ArrowLink from '../atoms/arrowLink'
import theme from '../atoms/theme'
import Container from '../container'
import { ContentSetItemEvent } from '../molecules/contentSetItemEvent'

const { font, colours, animation, breakpoints, icon } = theme

type PageNode = {
  node: Queries.ContentfulPage
}

type EventNode = {
  node: Queries.ContentfulEvent
}

type ContentSetProps = {
  preTitle?: string
  title?: string
  allLink?: PageNode[] | EventNode[] | string
  short?: boolean
  readonly nodes?: Array<EventNode>
  //Above is for what's on page which is still hardcoded, below is for content tile block
  block?:
    | Queries.ContentfulContentTileBlock
    | Queries.ContentfulCuratedContentTileBlock
  dynamicSetNodes?: Array<EventNode | PageNode>
  hideSliderOnMobile?: boolean
}

enum DynamicContent {
  LEARNING = '3 most recent in learning category',
  LIVE = '3 current events and exhibitions',
  EXHIBITIONS = '3 most recent exhibitions',
}

export const ContentSetContainer = styled(Container)`
  margin-bottom: ${(props): string => (props.hideSliderOnMobile ? '2rem' : props.short ? '0' : '5rem')};
  @media only screen and ${breakpoints.fromNormalScreen} {
    margin-bottom: ${(props): string => (props.hideSliderOnMobile ? '3.6rem' : props.short ? '6.4rem' : '10rem')};
  }
`

export const ContentSetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2.4rem;

  @media only screen and ${breakpoints.toNormalScreen} {
    flex-direction: ${(props): string => (props.hideSliderOnMobile ? 'row' : 'column')};
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    align-items: flex-end;
    margin-bottom: 4.8rem;
  }

  h2 {
    @media only screen and ${breakpoints.fromNormalScreen} {
      margin-bottom: 0;
    }
  }
`

export const StyledImg = styled(GatsbyImage)`
  picture img {
    transform: scale(1);
    transition: transform ${animation.defaultLength} ease-in-out !important;
  }
`

export const StyledArrowLink = styled(ArrowLink)`
  margin-bottom: 0;
`

const ContentSetItemsContainer = styled(Slider)`
  display: flex;
  margin-left: -2rem;
  margin-right: -2rem;

  @media only screen and ${breakpoints.fromNormalScreen} {
    margin-left: -1.2rem;
    margin-right: -1.2rem;
  }

  .slick-list {
    width: 100%;

    .slick-track {
      @media only screen and ${breakpoints.fromNormalScreen} {
        display: grid;
        grid-template-columns: repeat(3, 1fr);

        :before {
          content: unset;
        }
      }

      .slick-slide {
        @media only screen and ${breakpoints.fromNormalScreen} {
          width: 100% !important;
        }
        > div {
          margin-right: 1.2rem;
          margin-left: 1.2rem;
        }
      }
    }
  }

  a {
    text-decoration: none;
  }
`

const ContentSetItemsContainerWithoutSlider = styled.div`
  display: flex;
  flex-direction: column;

  @media only screen and ${breakpoints.fromNormalScreen} {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 2.4rem;
  }

  a {
    text-decoration: none;
  }
`

const ControlButtons = styled.div`
  display: ${(props): string => (props.hideOnMobile ? 'none' : 'flex')};
  justify-content: flex-end;
  margin-top: 1rem;

  @media only screen and ${breakpoints.fromNormalScreen} {
    display: none;
  }

  .MuiSvgIcon-root {
    width: 3.5rem;
    height: 3.5rem;
    margin-left: 2.4rem;

    @media only screen and ${breakpoints.fromNormalScreen} {
      width: ${icon.largeSize};
      height: ${icon.largeSize};
    }

    &:hover {
      cursor: pointer;
      color: ${colours.primary};
    }
  }
`

export const Times = styled.p`
  font-size: ${font.fontSizeDesktop};
`

export const ContentSet = (props: ContentSetProps): JSX.Element => {
  const slider = createRef<Slider>()
  const getNodesByKey = (key) => props.dynamicSetNodes[key]
  const showLeadingParagraph = props.hideSliderOnMobile
  let nodes = props.nodes || []

  if (props.block) {
    switch (props.block.dynamicContent) {
      case DynamicContent.LEARNING:
        nodes = getNodesByKey('learningSectionItems')
        break
      case DynamicContent.LIVE:
        nodes = getNodesByKey('whatsOnSectionItems')
        break
      case DynamicContent.EXHIBITIONS:
        nodes = getNodesByKey('exhibitionSectionItems')
        break
    }
  }

  // trim nodes to 6
  if (nodes.length > 6) {
    nodes = nodes.slice(0, 6)
  }

  const settings = {
    arrows: false,
    dots: false,
    fade: false,
    draggable: true,
    lazyLoad: 'progressive',
    slidesToShow: nodes.length,
    slidesToScroll: 6,
    speed: 1000,
    autoplay: false,
    autoplaySpeed: 4000,
    infinite: true,
    responsive: [
      {
        breakpoint: 1000,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: true,
          centerPadding: '40px',
          infinite: true,
        },
      },
    ],
  }

  const goToNextSlide = (): void => {
    if (slider.current) {
      slider.current.slickNext()
    }
  }

  const goToPreviousSlide = (): void => {
    if (slider.current) {
      slider.current.slickPrev()
    }
  }

  return (
    <ContentSetContainer
      short={props.short ? props.short : false}
      data-sal='slide-up'
      data-sal-duration='1000'
      hideSliderOnMobile={props.hideSliderOnMobile}
    >
      <ContentSetHeader hideSliderOnMobile={props.hideSliderOnMobile}>
        <H2>
          <AnimateText text={props.title || props.block.title || undefined} />
        </H2>
        <StyledArrowLink
          to={
            props.block ? props.block.linkUrl : props.allLink || props.linkUrl
          }
        >
          {props.block ? props.block.link : 'view more'}
        </StyledArrowLink>
      </ContentSetHeader>
      {props.hideSliderOnMobile ? (
        <ContentSetItemsContainerWithoutSlider>
          {nodes?.map((item, index) => {
            return item.node ? (
              <ContentSetItemEvent
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                key={`slider-${index}`}
                item={item.node}
                showLeadingText={showLeadingParagraph}
                withoutSlider={props.hideSliderOnMobile}
              />
            ) : (
              <ContentSetItemEvent
                // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                key={`slider-${index}`}
                item={item}
                showLeadingText={showLeadingParagraph}
                withoutSlider={props.hideSliderOnMobile}
              />
            )
          })}
        </ContentSetItemsContainerWithoutSlider>
      ) : (
        <ContentSetItemsContainer {...settings} ref={slider}>
          {nodes?.map((item, index) => {
            return item.node ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
              <ContentSetItemEvent key={`slider-${index}`} item={item.node} />
            ) : (
              // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
              <ContentSetItemEvent key={`slider-${index}`} item={item} />
            )
          })}
        </ContentSetItemsContainer>
      )}
      <ControlButtons hideOnMobile={props.hideSliderOnMobile}>
        <NavigateBefore onClick={(): void => goToPreviousSlide()} />
        <NavigateNext onClick={(): void => goToNextSlide()} />
      </ControlButtons>
    </ContentSetContainer>
  )
}
